import { Observable, delay, of, throwError, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Stabile Keys für die Dummy-Facades des Projekts (Sprint 3 Tag 0).
 *
 * Wenn ein Key in `environment.dummyErrorFacades` aufgeführt ist, liefern
 * alle Calls der zugehörigen Facade nach `dummyLatencyMs` einen Error
 * statt der Dummy-Daten. Damit lassen sich in Demos/Review-Sessions
 * Error- und Retry-States zeigen, ohne das Backend künstlich killen zu
 * müssen.
 *
 * Admin-Keys folgen in Sprint 3 Tag 1+, sobald die Admin-Facades stehen.
 */
export type DummyFacadeKey =
  | 'customer.depot'
  | 'customer.transactions'
  | 'customer.documents'
  | 'customer.profile'
  | 'admin.customers'
  | 'admin.transactions'
  | 'admin.documents'
  | 'admin.storageFees'
  | 'audit.logs';

export function shouldSimulateError(key: DummyFacadeKey): boolean {
  return environment.dummyErrorFacades?.includes(key) ?? false;
}

/**
 * Effektive Dummy-Latenz. Überschreibbar per URL-Query `?latency=<ms>`,
 * was das Visual-Regression-Spec für Skeleton-States (Sprint 3 Tag 0) und
 * manuelle Demos nutzen, um den Lade-Zustand verlängert sichtbar zu machen.
 * Ohne Query-Param greift `environment.dummyLatencyMs`.
 */
function resolveLatency(): number {
  if (typeof window === 'undefined') return environment.dummyLatencyMs;
  const raw = new URL(window.location.href).searchParams.get('latency');
  if (raw === null) return environment.dummyLatencyMs;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : environment.dummyLatencyMs;
}

/**
 * Standard-Response einer Dummy-Facade: nach `dummyLatencyMs` wird `value`
 * emittiert. Ist `key` in `environment.dummyErrorFacades` enthalten, wird
 * stattdessen nach derselben Verzögerung ein Error geworfen.
 *
 * Für Fälle, in denen vor dem Emit noch Seiteneffekte laufen (z. B.
 * `create` mit Store-Mutation), `shouldSimulateError()` + `dummyError()`
 * direkt verwenden, damit die Mutation im Error-Pfad nicht passiert.
 */
export function dummyResponse<T>(key: DummyFacadeKey, value: T): Observable<T> {
  const latency = resolveLatency();
  return shouldSimulateError(key)
    ? timer(latency).pipe(
        switchMap(() => throwError(() => new Error(`Dummy-Error (${key})`))),
      )
    : of(value).pipe(delay(latency));
}

export function dummyError<T>(key: DummyFacadeKey): Observable<T> {
  return timer(resolveLatency()).pipe(
    switchMap(() => throwError(() => new Error(`Dummy-Error (${key})`))),
  );
}
